import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  signUp,
  withTextLocalizer,
  selectBrandSettings,
  LocalizePropType,
  DEFAULT_PASSWORD_REGEX,
  selectDialog,
  selectPreSignupBooking,
  selectDeviceLanguage,
  selectUser,
  setNotification,
  clearAuth,
  getNextUserOpenChallengeRoute,
  getInvitationDetails,
  correctEmailDomain,
  useBubMigration,
  isUBQ,
} from 'getaway-data-layer';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import SignupView from './SignupView';
import { GA_TITLES, navigateTo, reportGAEvent } from '../../../utils';
import { showSignupTermsDialog } from '../../../utils/dialogsHelpers';
import {
  SignupModal,
  SignupSchema,
  SIGNUP_TYPES,
  LEGAL_TYPES,
} from '../../../validation';
import { useQuery } from '../../../hooks/useQuery';
import { PRIVACY_POLICY_STATUS } from '../../../enums';
import { generateMagicLink } from 'getaway-data-layer/src/redux/store/userData/user/actions';
import { setShowBubMigrationModal } from 'getaway-data-layer/src/redux/store/ui/map/Actions';
import { isAfter } from 'date-fns';

interface Values {
  // org
  companyName?: string;
  legalType?: string;
  otherLegalName?: string;
  registerNr?: string;
  registerLocation?: string;

  // private
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

type Props = {
  localize: LocalizePropType;
  containerStyles: any;
  showBack: boolean;
};

const RESET_DURATION = 30;

const SignupContainer = ({
  localize,
  containerStyles,
  showBack = true,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const brandSettings = useSelector(selectBrandSettings);
  const user = useSelector(selectUser);
  const locale = useSelector(selectDeviceLanguage);
  const orgSignupDisabled =
    brandSettings?.userOnboardingSettings?.orgSignupDisabled;
  const legacyOrgURL = brandSettings?.userOnboardingSettings?.legacyOrgURL;
  const privacyPolicyConsentRequired =
    brandSettings?.privacyPolicyConsentRequired;
  const preSignupBooking = useSelector(selectPreSignupBooking);
  const selectedDialog = useSelector(selectDialog);
  const query = useQuery();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [signupType, setSignupType] = useState(
    query.get('type') === 'org' ? SIGNUP_TYPES.ORG : SIGNUP_TYPES.PRIVATE
  );

  const [invitedByName, setInvitedByName] = useState<string | null>(null);
  const mainCustomerId = query.get('invitedBy');
  const mainCustomerHash = query.get('invitationKey');
  const showInvitationBox = !!mainCustomerHash && invitedByName;

  const [showPassword, setShowPassword] = useState(false);
  const [privacyPolicyStatus, setPrivacyPolicyStatus] = useState(
    PRIVACY_POLICY_STATUS.OPEN
  );
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null
  );
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [values, setValues] = useState<string | null>(null);
  const isWidget = query?.get('mode') === 'widget';

  const circleCode = query?.get('id') || query?.get('circleCode');
  const [resetCountdown, setResetCountdown] = useState(RESET_DURATION) as any;

  const [isEmailMissTyped, setIsEmailMissTyped] = useState(false);
  const [correctedEmail, setCorrectedEmail] = useState('');
  const showCountryRadio =
    // @ts-ignore
    brandSettings?.migrationDate &&
    // @ts-ignore
    isAfter(new Date(), new Date(brandSettings?.migrationDate));

  const { isDeUbqShutdown, formattedUbqShutdownDate, onShutdownCTA } =
    useBubMigration(isUBQ());

  const isSignInWithEmailEnabled =
    brandSettings?.accountSettings?.signInWithEmailEnabled;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const cleanupTurnstile = () => {
    if (turnstileWidgetId && window.turnstile) {
      try {
        // @ts-ignore
        window.turnstile.remove(turnstileWidgetId);
      } catch (e) {
        console.error('Error removing turnstile widget:', e);
      }
    }
    setTurnstileWidgetId(null);
    setTurnstileToken(null);
  };

  const submitSignup = (values: any, token: string = null) => {
    const { firstName, lastName, email, password } = values;
    const signUpInput = {
      ...values,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      password: password.trim(),
      userType:
        signupType === SIGNUP_TYPES.ORG
          ? 'org'
          : signupType === SIGNUP_TYPES.solopreneur
          ? 'solopreneur'
          : 'user',
      // business: true,
      locale,
      ...(token && { captchaToken: token }),
      ...(mainCustomerId && { mainCustomerId }),
      ...(mainCustomerHash && { mainCustomerHash }),
    };
    if (circleCode) {
      // @ts-ignore
      signUpInput.circleCode = circleCode;
    }
    dispatch(
      signUp(
        signUpInput,
        localize,
        (user) => {
          if (isWidget) {
            dispatch(
              generateMagicLink(false, (res) => {
                const link = `${window?.location?.origin}/onboarding/email?${
                  res?.value?.split('?')?.[1]
                }`;
                setMagicLink(link);
                window.open(link, '_blank');
              })
            );
          } else if (brandSettings?.onboardingType === 'onBooking') {
            navigateTo(history, '/map');
          } else {
            navigateTo(history, getNextUserOpenChallengeRoute(user));
          }
          reportGAEvent(GA_TITLES.AUTH.SIGN_UP_SUBMITTED);
        },
        query.get('utm_source'),
        query.get('utm_medium'),
        () => {
          // Duplicate callback - clean up Turnstile so user can get fresh widget on next submit
          cleanupTurnstile();
          setTimeout(() => {
            if (isWidget) {
              SignupFormik.resetForm();
              localStorage?.clear();
              dispatch(clearAuth());
              window.open(`${window?.location?.origin}/login`, '_blank');
            } else {
              if (isSignInWithEmailEnabled) {
                navigateTo(
                  history,
                  '/email-login?email=' +
                    encodeURIComponent(SignupFormik.values.email.trim())
                );
              } else {
                navigateTo(history, '/login');
              }
            }
          }, 100);
        },
        () => {
          // Error callback - clean up Turnstile for fresh widget on retry
          cleanupTurnstile();
          if (isWidget) {
            SignupFormik.resetForm();
            localStorage?.clear();
            dispatch(clearAuth());
          }
        }
      )
    );
  };

  const turnstileCheck = () => {
    // Clean up any existing widget before creating a new one
    cleanupTurnstile();

    // Create new widget
    const widgetId = window.turnstile.render(turnstileRef.current, {
      sitekey: process.env.VITE_TURNSTILE_SITE_KEY,
      callback: (token: string) => {
        setTurnstileToken(token);
      },
      theme: 'light',
      retry: 'never',
    });

    setTurnstileWidgetId(widgetId);
  };

  const proceedWithSignup = (values: any) => {
    setValues(values);
    if (
      privacyPolicyConsentRequired &&
      privacyPolicyStatus === PRIVACY_POLICY_STATUS.OPEN
    ) {
      setPrivacyPolicyStatus(PRIVACY_POLICY_STATUS.ERROR);
    } else if (
      !privacyPolicyConsentRequired ||
      (privacyPolicyStatus === PRIVACY_POLICY_STATUS.APPROVED &&
        privacyPolicyConsentRequired)
    ) {
      if (
        brandSettings?.captchaOnSignupEnabled &&
        process.env?.VITE_TURNSTILE_SITE_KEY &&
        window.turnstile &&
        window.location.hostname !== 'localhost'
      ) {
        turnstileCheck();
      } else {
        submitSignup(values);
      }
    }
  };

  const onSubmit = (values: any) => {
    proceedWithSignup(values);
  };

  const SignupFormik = useFormik({
    initialValues: SignupModal(signupType),
    validationSchema: SignupSchema(
      signupType,
      localize,
      brandSettings?.passwordRegex || DEFAULT_PASSWORD_REGEX
    ),
    onSubmit,
  });

  const onClose = () => {
    if (signupType === SIGNUP_TYPES.ORG) {
      setSignupType(SIGNUP_TYPES.PRIVATE);
    } else if (preSignupBooking) {
      history.goBack();
    } else {
      window.location.replace(location.origin);
    }
  };

  const showAboutDialog = () => {
    showSignupTermsDialog(localize);
  };

  const onPressPrivacyPolicyConsent = () => {
    setPrivacyPolicyStatus(
      privacyPolicyStatus === PRIVACY_POLICY_STATUS.APPROVED
        ? PRIVACY_POLICY_STATUS.OPEN
        : PRIVACY_POLICY_STATUS.APPROVED
    );
  };

  const onChangeSignupType = (e) => {
    if (e.target.value === SIGNUP_TYPES.COMPANY) {
      if (!orgSignupDisabled) {
        if (isWidget) {
          window.open(`${window?.location?.origin}/org-signup`, '_blank');
        } else {
          history.replace('/org-signup');
        }
      } else {
        window.open(
          legacyOrgURL || localize('signup.type.company.url'),
          '_blank'
        );
      }
      reportGAEvent(GA_TITLES.AUTH.SIGN_UP_VIA_COMPANY_CLICKED);
    } else if (e.target.value === SIGNUP_TYPES.CO_USER) {
      window.open(localize('signup.type.co_user.url'), '_blank');
      reportGAEvent(GA_TITLES.AUTH.SIGN_UP_AS_CO_USER);
    } else if (e.target.value === SIGNUP_TYPES.BIKE) {
      window.open(localize('signup.type.bike.url'), '_blank');
      reportGAEvent(GA_TITLES.AUTH.SIGN_UP_AS_CARGO_BIKE);
    } else if (e.target.value === SIGNUP_TYPES.ORG) {
      if (!orgSignupDisabled) {
        if (isWidget) {
          window.open(`${window?.location?.origin}/org-signup`, '_blank');
        } else {
          history.replace('/org-signup');
        }
      } else {
        window.open(legacyOrgURL || localize('signup.type.org.url'), '_blank');
      }
      reportGAEvent(GA_TITLES.AUTH.SIGN_UP_AS_ORG);
    } else if (e.target.value === SIGNUP_TYPES.PARTNER) {
      window.open(localize('signup.type.partner.url'), '_blank');
      reportGAEvent(GA_TITLES.AUTH.SIGN_UP_AS_PARTNER);
    } else if (e.target.value === SIGNUP_TYPES.ALTERNATIVE) {
      window.open(localize('signup.type.alternative.url'), '_blank');
    }
  };
  const onChangeLegalType = (e) => {
    SignupFormik.setFieldValue('legalType', e.target.value);
  };

  const onPressLogin = () => {
    isSignInWithEmailEnabled && window.location.hostname !== 'localhost'
      ? navigateTo(history, '/email-login')
      : navigateTo(history, '/login');
  };

  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    const trimmedValue = value.trim();

    const lastWord = trimmedValue.split(' ').pop();

    if (Object.values(LEGAL_TYPES).includes(lastWord)) {
      SignupFormik.setFieldValue('legalType', lastWord);
      SignupFormik.setFieldValue(
        'companyName',
        trimmedValue.replace(new RegExp(`\\s*${lastWord}$`), '')
      );
    } else {
      SignupFormik.setFieldValue('companyName', value);
    }
  };

  const onPressCopyLink = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink).then(() => {
        dispatch(setNotification({ message: localize('wj.link.copied') }));
      });
    }
  };

  const openMagicLink = () => {
    if (magicLink) window.open(magicLink, '_blank');
  };

  const onPressReset = () => {
    SignupFormik.resetForm();
    localStorage?.clear();
    dispatch(clearAuth());
    cleanupTurnstile();
  };

  useEffect(() => {
    if (!selectedDialog) {
      SignupFormik.setFieldValue('signupType', SIGNUP_TYPES.PRIVATE);
    }
  }, [selectedDialog]);

  useEffect(() => {
    SignupFormik.setValues(SignupModal(signupType));
  }, [signupType]);

  useEffect(() => {
    if (values && turnstileToken) {
      submitSignup(values, turnstileToken);
      // Clear temporary values to prevent re-submission if token changes
      // This does NOT clear the form - form values are in SignupFormik.values
      setValues(null);
    }
  }, [turnstileToken, values]);

  useEffect(() => {
    let countdownUpdaterId: any;

    if (user) {
      setResetCountdown(RESET_DURATION);

      countdownUpdaterId = setInterval(() => {
        setResetCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            onPressReset();
            clearInterval(countdownUpdaterId);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(countdownUpdaterId);
    };
  }, [user]);

  useEffect(() => {
    if (mainCustomerHash) {
      dispatch(
        getInvitationDetails(mainCustomerHash, (res) => {
          setInvitedByName(res.invitedBy);
        })
      );
    }
  }, []);

  // Clean up Turnstile on unmount
  useEffect(() => {
    return () => {
      cleanupTurnstile();
    };
  }, []);

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    SignupFormik.handleChange(e);

    const corrected = correctEmailDomain(emailValue);
    const isTypo = corrected !== emailValue;

    if (isTypo !== isEmailMissTyped || corrected !== correctedEmail) {
      setIsEmailMissTyped(isTypo);
      setCorrectedEmail(isTypo ? corrected : '');
    }
  };

  const onCountryValueChange = (value) => {
    if (value === 'AT') dispatch(setShowBubMigrationModal(true));
  };

  return (
    <SignupView
      localize={localize}
      formik={SignupFormik}
      signupType={signupType}
      containerStyles={containerStyles}
      showBack={showBack}
      showPassword={showPassword}
      showLoginLink={!!preSignupBooking}
      privacyPolicyStatus={privacyPolicyStatus}
      privacyPolicyConsentRequired={privacyPolicyConsentRequired}
      isWidget={isWidget}
      user={user}
      magicLink={magicLink}
      resetCountdown={resetCountdown}
      onPressReset={onPressReset}
      onPressCopyLink={onPressCopyLink}
      onClose={onClose}
      showAboutDialog={showAboutDialog}
      handleClickShowPassword={handleClickShowPassword}
      handleMouseDownPassword={handleMouseDownPassword}
      onChangeSignupType={onChangeSignupType}
      onPressLogin={onPressLogin}
      onPressPrivacyPolicyConsent={onPressPrivacyPolicyConsent}
      onChangeLegalType={onChangeLegalType}
      handleCompanyNameChange={handleCompanyNameChange}
      turnstileRef={turnstileRef}
      openMagicLink={openMagicLink}
      showInvitationBox={!!showInvitationBox}
      invitedByName={invitedByName}
      isEmailMissTyped={isEmailMissTyped}
      correctedEmail={correctedEmail}
      handleEmailChange={handleEmailChange}
      onCountryValueChange={onCountryValueChange}
      showCountryRadio={showCountryRadio}
      isDeUbqShutdown={isDeUbqShutdown}
      formattedUbqShutdownDate={formattedUbqShutdownDate}
      onShutdownCTA={onShutdownCTA}
      isTurnstileLoading={!!turnstileWidgetId}
    />
  );
};

export default withTextLocalizer(SignupContainer);
